import {AfterViewInit, Directive, ElementRef, NgZone, OnDestroy, Renderer2} from '@angular/core';

/**
 * Makes `.app-toolbar-center` survive a narrow window.
 *
 * The centre section of every app toolbar (filters, dividers, search,
 * actions) is a nowrap flex row. Once the window gets narrow enough the
 * responsive tiers in `src/app-toolbar-system.css` run out of things to shrink and
 * the row simply got clipped — whatever sat at the start of the row was
 * invisible and unreachable.
 *
 * This directive turns that clipped row into a horizontal scroller and hangs
 * a chevron on each side of it. The chevrons only appear when there really is
 * something hidden on that side, so a toolbar that fits looks exactly as it
 * did before.
 *
 * It has a CSS selector rather than an attribute selector, so every existing
 * `<div class="app-toolbar-center">` in the app gets the behaviour without a
 * template change. Only the matching CSS in `src/app-toolbar-system.css` and the
 * declaration in `app.module.ts` are needed.
 *
 * Scrolling is available through:
 *   - the chevron buttons (click, smooth scroll by ~60% of the visible width)
 *   - the mouse wheel / trackpad while hovering the toolbar
 *   - touch / drag (native overflow scrolling)
 *   - keyboard focus — tabbing to a control inside the row scrolls it in
 *
 * The chevrons are `aria-hidden` and not tabbable, mirroring what Material's
 * own tab-header pagination does: they are a pointer affordance for a region
 * that assistive technology and the keyboard can already reach.
 */
@Directive({
    selector: '.app-toolbar-center'
})
export class ToolbarCenterScrollDirective implements AfterViewInit, OnDestroy {

    /** Smallest overflow (px) worth reacting to — guards against sub-pixel rounding. */
    private static readonly EPSILON = 2;

    private readonly host: HTMLElement;

    private startButton: HTMLButtonElement | null = null;
    private endButton: HTMLButtonElement | null = null;

    private resizeObserver: ResizeObserver | null = null;
    private mutationObserver: MutationObserver | null = null;
    private readonly teardown: Array<() => void> = [];

    private pendingFrame = 0;
    private isRtl = false;

    constructor(elementRef: ElementRef<HTMLElement>,
                private renderer: Renderer2,
                private zone: NgZone) {
        this.host = elementRef.nativeElement;
    }

    ngAfterViewInit(): void {
        if (!this.host || !this.host.parentNode) {
            return;
        }

        this.isRtl = (this.host.ownerDocument.documentElement.getAttribute('dir') === 'rtl')
            || getComputedStyle(this.host).direction === 'rtl';

        // Everything here is pure DOM bookkeeping — no template bindings are
        // touched — so keep it out of the zone and away from change detection.
        this.zone.runOutsideAngular(() => {
            this.createButtons();
            this.listen(this.host, 'scroll', () => this.scheduleUpdate());
            this.listen(this.host, 'wheel', (event: Event) => this.onWheel(event as WheelEvent));
            this.listen(this.host, 'focusin', (event: Event) => this.onFocusIn(event));
            this.listen(window, 'resize', () => this.scheduleUpdate());
            this.listen(this.host, 'transitionend', () => this.scheduleUpdate());

            this.observeSizes();
            this.observeChildren();

            // Icon fonts land after the first layout pass and change the
            // measured width of every button in the row.
            const fonts = (this.host.ownerDocument as any).fonts;
            if (fonts && typeof fonts.ready?.then === 'function') {
                fonts.ready.then(() => this.scheduleUpdate());
            }

            this.scheduleUpdate();
        });
    }

    ngOnDestroy(): void {
        if (this.pendingFrame) {
            cancelAnimationFrame(this.pendingFrame);
            this.pendingFrame = 0;
        }
        this.teardown.forEach(off => off());
        this.teardown.length = 0;

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
        }
        this.removeButton(this.startButton);
        this.removeButton(this.endButton);
        this.startButton = null;
        this.endButton = null;
    }

    // ---------------------------------------------------------------- setup

    private listen(target: EventTarget, event: string, handler: (e: Event) => void): void {
        const options: AddEventListenerOptions = event === 'wheel' ? {passive: false} : {passive: true};
        target.addEventListener(event, handler, options);
        this.teardown.push(() => target.removeEventListener(event, handler, options as any));
    }

    private observeSizes(): void {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
        this.resizeObserver.observe(this.host);
        this.observeChildSizes();
    }

    /** Re-point the ResizeObserver at the current children (search box grows on focus, etc.). */
    private observeChildSizes(): void {
        if (!this.resizeObserver) {
            return;
        }
        this.resizeObserver.disconnect();
        this.resizeObserver.observe(this.host);
        for (let i = 0; i < this.host.children.length; i++) {
            this.resizeObserver.observe(this.host.children[i]);
        }
    }

    /** Toolbar contents are frequently `*ngIf`-ed in and out. */
    private observeChildren(): void {
        if (typeof MutationObserver === 'undefined') {
            return;
        }
        this.mutationObserver = new MutationObserver(() => {
            this.observeChildSizes();
            this.scheduleUpdate();
        });
        this.mutationObserver.observe(this.host, {childList: true, subtree: true, characterData: true});
    }

    private createButtons(): void {
        this.startButton = this.createButton('start');
        this.endButton = this.createButton('end');

        const parent = this.host.parentNode as HTMLElement;
        // DOM order is logical, not visual: in RTL the flex row mirrors these
        // for free, and the chevron glyphs are flipped in CSS.
        parent.insertBefore(this.startButton, this.host);
        parent.insertBefore(this.endButton, this.host.nextSibling);
    }

    private createButton(side: 'start' | 'end'): HTMLButtonElement {
        const button: HTMLButtonElement = this.renderer.createElement('button');
        this.renderer.setAttribute(button, 'type', 'button');
        this.renderer.setAttribute(button, 'tabindex', '-1');
        this.renderer.setAttribute(button, 'aria-hidden', 'true');
        this.renderer.addClass(button, 'app-toolbar-scroll-btn');
        this.renderer.addClass(button, 'app-toolbar-scroll-btn-' + side);
        // Inline SVG rather than an icon ligature: this element is built
        // outside the Angular compiler, so <mat-icon> would never be upgraded.
        button.innerHTML = '<svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">'
            + '<path d="' + (side === 'start' ? 'M15 5 L8 12 L15 19' : 'M9 5 L16 12 L9 19') + '"'
            + ' fill="none" stroke="currentColor" stroke-width="2.2"'
            + ' stroke-linecap="round" stroke-linejoin="round"/></svg>';
        button.addEventListener('click', () => this.scrollBySide(side));
        return button;
    }

    private removeButton(button: HTMLElement | null): void {
        if (button && button.parentNode) {
            button.parentNode.removeChild(button);
        }
    }

    // --------------------------------------------------------------- behaviour

    private scrollBySide(side: 'start' | 'end'): void {
        const step = Math.max(96, Math.round(this.host.clientWidth * 0.6));
        // `scrollLeft` runs 0 → -max in RTL, so moving toward the start of the
        // content is a positive delta there and a negative one in LTR.
        const towardStart = this.isRtl ? step : -step;
        const delta = side === 'start' ? towardStart : -towardStart;
        this.host.scrollBy({left: delta, behavior: 'smooth'});
    }

    private onWheel(event: WheelEvent): void {
        if (!this.isOverflowing()) {
            return;
        }
        // A plain vertical wheel over the toolbar should pan it sideways;
        // trackpads that already send deltaX are left alone.
        if (event.deltaX !== 0 || event.deltaY === 0 || event.ctrlKey) {
            return;
        }
        const before = this.host.scrollLeft;
        this.host.scrollLeft = before + (this.isRtl ? -event.deltaY : event.deltaY);
        if (this.host.scrollLeft !== before) {
            event.preventDefault();
        }
    }

    private onFocusIn(event: Event): void {
        const target = event.target as HTMLElement;
        if (target && typeof target.scrollIntoView === 'function' && this.isOverflowing()) {
            target.scrollIntoView({block: 'nearest', inline: 'nearest'});
        }
    }

    private isOverflowing(): boolean {
        return (this.host.scrollWidth - this.host.clientWidth) > ToolbarCenterScrollDirective.EPSILON;
    }

    private scheduleUpdate(): void {
        if (this.pendingFrame) {
            return;
        }
        this.pendingFrame = requestAnimationFrame(() => {
            this.pendingFrame = 0;
            this.update();
        });
    }

    private update(): void {
        if (!this.host.isConnected) {
            return;
        }
        const max = this.host.scrollWidth - this.host.clientWidth;
        const overflowing = max > ToolbarCenterScrollDirective.EPSILON;
        const position = Math.abs(this.host.scrollLeft);

        const hiddenAtStart = overflowing && position > ToolbarCenterScrollDirective.EPSILON;
        const hiddenAtEnd = overflowing && position < (max - ToolbarCenterScrollDirective.EPSILON);

        this.toggle(this.startButton, hiddenAtStart);
        this.toggle(this.endButton, hiddenAtEnd);

        // The host classes drive the edge fade in src/app-toolbar-system.css.
        this.host.classList.toggle('toolbar-overflow-start', hiddenAtStart);
        this.host.classList.toggle('toolbar-overflow-end', hiddenAtEnd);
    }

    private toggle(button: HTMLElement | null, visible: boolean): void {
        if (button) {
            button.classList.toggle('is-visible', visible);
        }
    }
}




